import dayjs from "dayjs";
import { formatNumber } from "../../../utils/format";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";
import Layout from "../../../layout/Layout";
import { AlertMessage } from "../../../components/AlertMessage";
import { apiData } from "../../../services/api_service";
import {
  createMissingStockAPI,
  fetchMissingStockAPI,
  missingStockBaseAPI,
  storesDropdownAPI,
  fetchProductDropdownAPI,
  fetchCustomerDDAPI,
  termsAndConditionsAPI,
  productAvailabilityAPI,
} from "../../../config";

// "Missing Stock": flag a physical-count shortfall against what the portal
// shows (subtracts from Available immediately, never touches Total — see
// missingStockModel.js on the backend), and later convert it to a zero-cost
// (FOC) invoice against the customer it actually went to, once known. That
// conversion is just a normal FOC request under the hood, so approval,
// stock deduction and the FOC report are all the existing pipeline —
// nothing new to build there.
const MissingStockList = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const alertRef = useRef();

  const [tblList, setTblList] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [loading, setLoading] = useState(false);

  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [terms, setTerms] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    store_id: "",
    product: null,
    quantity: "",
    reason: "",
  });
  const [addSaving, setAddSaving] = useState(false);
  // Live stock numbers for the currently picked store+product, shown right
  // in the dialog so a shortfall (available already 0, etc.) is obvious
  // before submitting instead of only surfacing as a 409 after Save.
  const [availability, setAvailability] = useState(null); // { onHand, reserved, missing, atp }
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  const [convertRow, setConvertRow] = useState(null);
  const [convertForm, setConvertForm] = useState({
    customer: null,
    sellingPrice: "",
    focReason: "",
    term_id: "",
  });
  const [convertSaving, setConvertSaving] = useState(false);

  const fetchList = async () => {
    try {
      setLoading(true);
      const payload = {
        pagination: JSON.stringify(pagination),
        sorting: sorting.length ? JSON.stringify(sorting[0]) : null,
        search: columnFilters.length ? JSON.stringify(columnFilters) : null,
      };
      const result = await apiData("get", fetchMissingStockAPI, payload);
      if (result.success) {
        setTblList(result.data.result || []);
        setRowCount(result.data.totalCount || 0);
      } else {
        setTblList([]);
        setRowCount(0);
      }
    } catch (e) {
      console.error("Failed to fetch missing stock list", e);
    } finally {
      setLoading(false);
    }
  };

  // Export every row that matches the current filters/sort (not just the page
  // on screen), with the same columns shown in the table.
  const handleExportData = async () => {
    try {
      setLoading(true);
      const payload = {
        pagination: JSON.stringify({ pageIndex: 0, pageSize: 1000000 }),
        sorting: sorting.length ? JSON.stringify(sorting[0]) : null,
        search: columnFilters.length ? JSON.stringify(columnFilters) : null,
      };
      const result = await apiData("get", fetchMissingStockAPI, payload);
      const dataArray =
        result?.success && Array.isArray(result.data.result)
          ? result.data.result
          : [];
      if (!dataArray.length) {
        alertRef.current?.triggerAlert(
          JSON.stringify({ message: "No data to export!", messageType: "warning" })
        );
        return;
      }
      const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
      const exportData = dataArray.map((row, i) => ({
        "#": i + 1,
        "Product Name": row.product_name || "",
        SKU: row.sku || "",
        Store: row.store_name || "",
        Quantity: Number(row.quantity) || 0,
        Reason: row.reason || "",
        Status: cap(row.status),
        "FOC Request": row.foc_request_number || "",
        "Reported By": row.reported_by_name || "",
        "Reported At": row.reported_at
          ? dayjs(row.reported_at).format("DD-MM-YYYY HH:mm")
          : "",
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "Missing Stock");
      writeXlsxFrozen(XLSX, wb, `missing_stock_${dayjs().format("DD_MM_YYYY")}.xlsx`);
    } catch (e) {
      console.error("Export failed:", e);
      alertRef.current?.triggerAlert(
        JSON.stringify({ message: "Failed to export data.", messageType: "error" })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting, columnFilters]);

  useEffect(() => {
    (async () => {
      try {
        const storeResult = await apiData("get", storesDropdownAPI);
        if (storeResult.success) setStores(storeResult.data || []);
      } catch (e) {
        console.error("Failed to fetch stores:", e);
      }
    })();
    (async () => {
      try {
        const customerResult = await apiData("get", fetchCustomerDDAPI);
        if (customerResult.data?.result) setCustomers(customerResult.data.result);
      } catch (e) {
        console.error("Failed to fetch customers:", e);
      }
    })();
    (async () => {
      try {
        const termsResult = await apiData("get", termsAndConditionsAPI, { termsType: 2 });
        if (termsResult.success) setTerms(termsResult.data.result || []);
      } catch (e) {
        console.error("Failed to fetch terms:", e);
      }
    })();
  }, []);

  const fetchProducts = async (search = "") => {
    try {
      const result = await apiData("get", `${fetchProductDropdownAPI}?search=${search}`);
      if (result.success) setProducts(result.data.result || []);
    } catch (e) {
      console.error("Failed to fetch products:", e);
    }
  };

  // Pull live In Stock / Reserved / Missing (already pending) / Available
  // the moment both a store and a product are picked, so the dialog shows
  // exactly what the store report shows — no guessing, no surprise 409.
  useEffect(() => {
    if (!addOpen || !addForm.store_id || !addForm.product) {
      setAvailability(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setAvailabilityLoading(true);
        const result = await apiData("post", productAvailabilityAPI, {
          store_id: addForm.store_id,
          product_ids: [addForm.product.id],
        });
        if (!cancelled && result.success && Array.isArray(result.data)) {
          setAvailability(result.data[0] || null);
        }
      } catch (e) {
        console.error("Failed to fetch availability:", e);
        if (!cancelled) setAvailability(null);
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addOpen, addForm.store_id, addForm.product]);

  const triggerAlert = (message, messageType = "success") => {
    alertRef.current?.triggerAlert(JSON.stringify({ message, messageType }));
  };

  const handleOpenAdd = () => {
    setAddForm({ store_id: "", product: null, quantity: "", reason: "" });
    setAvailability(null);
    fetchProducts("");
    setAddOpen(true);
  };

  const handleSubmitAdd = async () => {
    if (!addForm.store_id || !addForm.product || !addForm.quantity) {
      triggerAlert("Store, Product and Quantity are required.", "error");
      return;
    }
    if (availability && Number(addForm.quantity) > availability.atp) {
      triggerAlert(
        `Cannot flag ${addForm.quantity} as missing — only ${availability.atp} currently available.`,
        "error"
      );
      return;
    }
    try {
      setAddSaving(true);
      const result = await apiData("post", createMissingStockAPI, {
        store_id: addForm.store_id,
        product_id: addForm.product.id,
        quantity: Number(addForm.quantity),
        reason: addForm.reason,
        user_id: user?.emp_id,
      });
      if (result.success) {
        triggerAlert(result.message, "success");
        setAddOpen(false);
        fetchList();
      } else {
        triggerAlert(result.message || "Failed to record missing stock", "error");
      }
    } catch (e) {
      triggerAlert(e?.response?.data?.message || "Failed to record missing stock", "error");
    } finally {
      setAddSaving(false);
    }
  };

  const handleCancelEntry = async (row) => {
    if (!window.confirm(`Cancel this missing-stock entry for ${row.product_name}?`)) return;
    try {
      const result = await apiData("post", `${missingStockBaseAPI}/${row.id}/cancel`, {
        user_id: user?.emp_id,
      });
      triggerAlert(result.message, result.success ? "success" : "error");
      if (result.success) fetchList();
    } catch (e) {
      triggerAlert("Failed to cancel entry", "error");
    }
  };

  const handleOpenConvert = (row) => {
    setConvertRow(row);
    const defaultTerm = terms.find((t) => t.isDefault === 1);
    setConvertForm({
      customer: null,
      sellingPrice: "",
      focReason: row.reason || "Missing stock write-off",
      term_id: defaultTerm?.id || "",
    });
  };

  const handleSubmitConvert = async () => {
    if (!convertForm.customer || !convertForm.term_id) {
      triggerAlert("Customer and Terms are required.", "error");
      return;
    }
    try {
      setConvertSaving(true);
      const result = await apiData("post", `${missingStockBaseAPI}/${convertRow.id}/convert-to-foc`, {
        customer_id: convertForm.customer.customer_id,
        sellingPrice: convertForm.sellingPrice || 0,
        focReason: convertForm.focReason,
        term_id: convertForm.term_id,
        terms: terms.find((t) => t.id === convertForm.term_id)?.description || "",
        user_id: user?.emp_id,
      });
      if (result.success) {
        triggerAlert(result.message, "success");
        setConvertRow(null);
        fetchList();
      } else {
        triggerAlert(result.message || "Failed to convert to FOC", "error");
      }
    } catch (e) {
      triggerAlert(e?.response?.data?.message || "Failed to convert to FOC", "error");
    } finally {
      setConvertSaving(false);
    }
  };

  const formatDateTime = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY HH:mm") : "NA";

  // Who/when for the status a row is currently in, shown as a tooltip on the
  // chip (same pattern as decided_by_name on the FOC Approvals status chip) —
  // keeps the table from needing a separate column per lifecycle stage.
  const statusChip = (row) => {
    const map = {
      pending: { color: "warning", label: "Pending" },
      converted: { color: "success", label: "Converted" },
      cancelled: { color: "default", label: "Cancelled" },
    };
    const cfg = map[row.status] || { color: "default", label: row.status };
    let tooltip = `Reported by ${row.reported_by_name || "—"} on ${formatDateTime(row.reported_at)}`;
    if (row.status === "cancelled") {
      tooltip += ` · Cancelled by ${row.cancelled_by_name || "—"} on ${formatDateTime(row.cancelled_at)}`;
    } else if (row.status === "converted") {
      tooltip += ` · Converted on ${formatDateTime(row.converted_at)} (${row.foc_request_number || ""})`;
    }
    return (
      <Tooltip title={tooltip} arrow>
        <Chip size="small" color={cfg.color} label={cfg.label} />
      </Tooltip>
    );
  };

  const columns = useMemo(
    () => [
      { accessorKey: "product_name", header: "Product Name", size: 10 },
      { accessorKey: "sku", header: "SKU", size: 10 },
      { accessorKey: "store_name", header: "Store", size: 10 },
      {
        accessorKey: "quantity",
        header: "Quantity",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => formatNumber(row.original.quantity),
      },
      { accessorKey: "reason", header: "Reason", size: 15, enableColumnFilter: false },
      {
        accessorKey: "status",
        header: "Status",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => statusChip(row.original),
      },
      {
        accessorKey: "foc_request_number",
        header: "FOC Request",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.foc_request_number ? (
            <Tooltip title={`FOC status: ${row.original.foc_status || ""}`} arrow>
              <span>{row.original.foc_request_number}</span>
            </Tooltip>
          ) : (
            "—"
          ),
      },
      { accessorKey: "reported_by_name", header: "Reported By", size: 10, enableColumnFilter: false },
      {
        accessorKey: "reported_at",
        header: "Reported At",
        size: 12,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatDateTime(cell.getValue()),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 15,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          if (row.original.status !== "pending") return null;
          return (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" onClick={() => handleOpenConvert(row.original)}>
                Convert to FOC
              </Button>
              <Button size="small" color="error" onClick={() => handleCancelEntry(row.original)}>
                Cancel
              </Button>
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [terms]
  );

  return (
    <Layout>
      <AlertMessage ref={alertRef} />
      <Grid className="employee-form">
        <Grid container spacing={3} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md={6}>
            <Typography className="page_title" textAlign="left">
              Missing Stock
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} textAlign="right">
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportData}
              sx={{ mr: 1 }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleOpenAdd}
            >
              Flag Missing Stock
            </Button>
          </Grid>
        </Grid>

        <Grid mt={2}>
          <MaterialReactTable
            columns={columns}
            data={tblList}
            enableRowNumbers
            enableColumnPinning
            enableStickyHeader
            manualFiltering
            manualPagination
            manualSorting
            onColumnFiltersChange={setColumnFilters}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            rowCount={rowCount}
            state={{ columnFilters, isLoading: loading, pagination, sorting }}
            initialState={{ showColumnFilters: true }}
            muiTableContainerProps={{
              sx: { maxHeight: "calc(100vh - 240px)", minHeight: 450, overflowY: "auto" },
            }}
          />
        </Grid>
      </Grid>

      {/* Add Missing dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Flag Missing Stock</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Store"
                value={addForm.store_id}
                onChange={(e) => setAddForm((f) => ({ ...f, store_id: e.target.value }))}
              >
                {stores.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.store_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={products}
                getOptionLabel={(o) => o.product_name || ""}
                value={addForm.product}
                onChange={(e, v) => setAddForm((f) => ({ ...f, product: v }))}
                onInputChange={(e, v) => fetchProducts(v)}
                renderInput={(params) => <TextField {...params} label="Product" />}
              />
            </Grid>

            {addForm.store_id && addForm.product && (
              <Grid item xs={12}>
                {availabilityLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Checking current stock…
                  </Typography>
                ) : availability ? (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      flexWrap: "wrap",
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography variant="body2">
                      <strong>In Stock:</strong> {availability.onHand}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Reserved:</strong> {availability.reserved}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Already Missing (pending):</strong> {availability.missing}
                    </Typography>
                    <Typography variant="body2" sx={{ color: availability.atp > 0 ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                      Available: {availability.atp}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="error">
                    Could not load current stock for this product/store.
                  </Typography>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Quantity Missing"
                value={addForm.quantity}
                onChange={(e) => setAddForm((f) => ({ ...f, quantity: e.target.value }))}
                error={!!availability && Number(addForm.quantity) > availability.atp}
                helperText={
                  availability && Number(addForm.quantity) > availability.atp
                    ? `Only ${availability.atp} available`
                    : ""
                }
                inputProps={{ min: 1, max: availability?.atp || undefined }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Reason (optional)"
                value={addForm.reason}
                onChange={(e) => setAddForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              addSaving ||
              (!!availability && Number(addForm.quantity || 0) > availability.atp)
            }
            onClick={handleSubmitAdd}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to FOC dialog */}
      <Dialog open={!!convertRow} onClose={() => setConvertRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Convert to FOC Invoice</DialogTitle>
        <DialogContent>
          {convertRow && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {convertRow.quantity} x {convertRow.product_name} — {convertRow.store_name}
            </Typography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={customers}
                getOptionLabel={(o) => o.name || o.company_name || ""}
                value={convertForm.customer}
                onChange={(e, v) => setConvertForm((f) => ({ ...f, customer: v }))}
                renderInput={(params) => <TextField {...params} label="Customer" />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Selling Price (per unit, for reporting)"
                value={convertForm.sellingPrice}
                onChange={(e) => setConvertForm((f) => ({ ...f, sellingPrice: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Terms & Conditions"
                value={convertForm.term_id}
                onChange={(e) => setConvertForm((f) => ({ ...f, term_id: e.target.value }))}
              >
                {terms.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.title || t.name || `Term #${t.id}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="FOC Reason"
                value={convertForm.focReason}
                onChange={(e) => setConvertForm((f) => ({ ...f, focReason: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertRow(null)}>Cancel</Button>
          <Button variant="contained" disabled={convertSaving} onClick={handleSubmitConvert}>
            Submit for Approval
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default MissingStockList;
